import React, { useState, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Users, MapPin, Grid3X3 } from 'lucide-react';
import api from '../../services/api';
import { Toaster, toast } from 'sonner';

const ENTITY_TYPES = [
  { value: 'users', label: 'Colaboradores', icon: Users, description: 'Nombre, email, rol, sede, equipo, cargo', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'sedes', label: 'Sedes', icon: MapPin, description: 'Nombre, dirección, ciudad, capacidad', color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'spaces', label: 'Espacios', icon: Grid3X3, description: 'Nombre, tipo, zona, capacidad', color: 'bg-purple-50 text-purple-700 border-purple-200' },
];

const BulkUploadPage = () => {
  const [entityType, setEntityType] = useState('users');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      if (!selected.name.match(/\.(xlsx|xls)$/i)) {
        toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
        return;
      }
      setFile(selected);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/bulk-upload?entity_type=${entityType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      if (response.data.created > 0) {
        toast.success(`${response.data.created} registros creados exitosamente`);
      }
    } catch (error) {
      toast.error('Error al procesar el archivo');
      setResult({ created: 0, skipped: 0, errors: [error.message] });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/bulk-upload/template/${entityType}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_${entityType}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.name.match(/\.(xlsx|xls)$/i)) {
      setFile(dropped);
      setResult(null);
    } else {
      toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
    }
  };

  const currentEntity = ENTITY_TYPES.find(e => e.value === entityType);

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Carga Masiva
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Importa datos desde archivos Excel para configurar el sistema rápidamente
          </p>
        </div>

        {/* Entity Type Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ENTITY_TYPES.map((entity) => (
            <button
              key={entity.value}
              onClick={() => { setEntityType(entity.value); setFile(null); setResult(null); }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                entityType === entity.value 
                  ? 'border-[#005472] bg-[#EAF5FB]' 
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
              data-testid={`entity-${entity.value}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${entity.color}`}>
                  <entity.icon className="w-4 h-4" />
                </div>
                <span className="font-semibold text-sm">{entity.label}</span>
              </div>
              <p className="text-xs text-slate-400">{entity.description}</p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Area */}
          <div className="lg:col-span-2">
            <Card className="border border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#005472]" />
                  Subir Archivo Excel
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Drop zone */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                    file ? 'border-[#005472] bg-[#EAF5FB]' : 'border-slate-300 hover:border-[#5AB0DA] hover:bg-slate-50'
                  }`}
                  data-testid="drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-input"
                  />
                  {file ? (
                    <div className="space-y-2">
                      <FileSpreadsheet className="w-12 h-12 mx-auto text-[#005472]" />
                      <p className="font-semibold text-sm text-[#005472]">{file.name}</p>
                      <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 mx-auto text-slate-300" />
                      <p className="text-sm text-slate-500">
                        Arrastra tu archivo Excel aquí o <span className="text-[#005472] font-medium">haz click para seleccionar</span>
                      </p>
                      <p className="text-xs text-slate-400">Formatos soportados: .xlsx, .xls</p>
                    </div>
                  )}
                </div>

                {/* Upload button */}
                <div className="flex items-center gap-3 mt-4">
                  <Button
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    className="bg-[#005472] hover:bg-[#003D54] rounded-sm flex-1"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" />Cargar {currentEntity?.label}</>
                    )}
                  </Button>
                  {file && (
                    <Button
                      variant="outline"
                      onClick={() => { setFile(null); setResult(null); }}
                      className="rounded-sm"
                    >
                      Limpiar
                    </Button>
                  )}
                </div>

                {/* Results */}
                {result && (
                  <div className="mt-6 space-y-3" data-testid="upload-result">
                    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{result.created}</p>
                        <p className="text-xs text-slate-500">Creados</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                        <p className="text-xs text-slate-500">Omitidos</p>
                      </div>
                      {result.errors?.length > 0 && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-600">{result.errors.length}</p>
                          <p className="text-xs text-slate-500">Errores</p>
                        </div>
                      )}
                    </div>
                    {result.created > 0 && (
                      <div className="flex items-center gap-2 text-sm text-green-700">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{result.created} registros importados exitosamente</span>
                      </div>
                    )}
                    {result.errors?.length > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-sm font-medium text-red-700 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          <span>Errores encontrados:</span>
                        </div>
                        <ul className="space-y-1 max-h-32 overflow-y-auto">
                          {result.errors.map((err, i) => (
                            <li key={i} className="text-xs text-red-600">{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Instructions Panel */}
          <div className="space-y-4">
            <Card className="border border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Plantilla Excel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">
                  Descarga la plantilla con las columnas correctas y datos de ejemplo para {currentEntity?.label.toLowerCase()}.
                </p>
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  className="w-full rounded-sm gap-2"
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4" />
                  Descargar Plantilla
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Instrucciones</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 text-sm text-slate-600">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EAF5FB] text-[#005472] flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <span>Descarga la plantilla Excel</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EAF5FB] text-[#005472] flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <span>Completa los datos respetando las columnas</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EAF5FB] text-[#005472] flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <span>Sube el archivo Excel al sistema</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EAF5FB] text-[#005472] flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                    <span>Revisa el resumen de resultados</span>
                  </li>
                </ol>
                {entityType === 'users' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <strong>Nota:</strong> Los colaboradores se crean con contraseña temporal <code className="bg-amber-100 px-1 rounded">password123</code>. Deben cambiarla en su primer ingreso.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Columnas requeridas</CardTitle>
              </CardHeader>
              <CardContent>
                {entityType === 'users' && (
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">nombre</span><span className="text-slate-400">Nombre completo</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">email</span><span className="text-slate-400">Correo corporativo</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">rol</span><span className="text-slate-400">colaborador / admin</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">sede</span><span className="text-slate-400">Nombre de la sede</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">equipo</span><span className="text-slate-400">Nombre del equipo</span></div>
                    <div className="flex justify-between py-1"><span className="font-medium">cargo</span><span className="text-slate-400">Cargo del colaborador</span></div>
                  </div>
                )}
                {entityType === 'sedes' && (
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">nombre</span><span className="text-slate-400">Nombre de la sede</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">direccion</span><span className="text-slate-400">Dirección completa</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">ciudad</span><span className="text-slate-400">Ciudad (Lima, Arequipa...)</span></div>
                    <div className="flex justify-between py-1"><span className="font-medium">capacidad</span><span className="text-slate-400">Número de puestos</span></div>
                  </div>
                )}
                {entityType === 'spaces' && (
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">nombre</span><span className="text-slate-400">Nombre del espacio</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">tipo</span><span className="text-slate-400">hot_desk / puesto_fijo / sala_reuniones</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="font-medium">zona</span><span className="text-slate-400">Nombre de la zona existente</span></div>
                    <div className="flex justify-between py-1"><span className="font-medium">capacidad</span><span className="text-slate-400">Capacidad del espacio</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BulkUploadPage;
